import { cn, Modal, Spinner } from '@crm/ui';
import { ArrowLeftRight, LogOut, Palette, UserPlus, UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getInitials } from '@/lib/utils';
import { SETTINGS_ROUTES } from '@/modules/settings/constants';
import { forgetAccount, type KnownAccount, knownAccounts, rememberAccount } from '../accounts';
import { useConnectedAccounts, useLogout, useSwitchAccount } from '../hooks';
import type { PublicUser } from '../types';
import { AccountPicker } from './account-picker';
import { AccountRow } from './account-row';

/**
 * As outras contas em uma lista so: as que continuam conectadas trocam sem
 * senha; as que so foram vistas neste navegador voltam pelo formulario.
 */
function accountsToShow(
  atual: PublicUser,
  conectadas: { id: string; name: string; email: string; role: string }[],
) {
  const vivas = conectadas
    .filter((conta) => conta.email !== atual.email)
    .map((conta) => ({ ...conta, image: null, conectada: true }));

  const conhecidas = knownAccounts()
    .filter(
      (conta) => conta.email !== atual.email && !vivas.some((viva) => viva.email === conta.email),
    )
    .map((conta) => ({ ...conta, conectada: false }));

  return [...vivas, ...conhecidas];
}

import { LoginForm } from './login-form';

/**
 * A lista de contas: a de agora, as que ja entraram neste navegador e o
 * caminho para outra. Quem escolhe uma conta so avisa - a janela de entrar
 * vive fora daqui, para nao sumir junto com o menu que fechou.
 */
export function AccountSwitcher({
  user,
  onEntrar,
}: {
  user: PublicUser;
  onEntrar: (alvo: KnownAccount | 'nova' | 'trocar') => void;
}) {
  useEffect(() => rememberAccount(user), [user]);

  return (
    <div>
      <AccountRow
        name={user.name}
        email={user.email}
        role={user.role}
        atual
        acao={
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            onClick={() => onEntrar('trocar')}
          >
            <ArrowLeftRight className="h-4 w-4" />
            Trocar
          </button>
        }
      />

      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-3 border-t border-border px-4 py-3 text-left text-sm transition-colors hover:bg-muted"
        onClick={() => onEntrar('nova')}
      >
        <UserPlus className="h-5 w-5 text-muted-foreground" />
        Adicionar conta
      </button>
    </div>
  );
}

/** A janela de entrar, em tela cheia no celular e janela no desktop. */
export function LoginModal({
  alvo,
  atual,
  onClose,
}: {
  alvo: KnownAccount | 'nova' | 'trocar';
  atual: PublicUser;
  onClose: () => void;
}) {
  const [escolhida, setEscolhida] = useState<KnownAccount | null>(
    typeof alvo === 'object' ? alvo : null,
  );
  const [esquecidas, setEsquecidas] = useState<string[]>([]);
  const [modo, setModo] = useState<'lista' | 'form'>(alvo === 'trocar' ? 'lista' : 'form');

  const conectadas = useConnectedAccounts();
  const trocar = useSwitchAccount();

  const outras = accountsToShow(atual, conectadas.data?.data ?? []).filter(
    (conta) => !esquecidas.includes(conta.id),
  );
  const escolhendo = modo === 'lista';

  return (
    <Modal
      open
      onClose={onClose}
      title={alvo === 'nova' ? 'Adicionar conta' : 'Trocar de conta'}
      subtitle={
        alvo === 'nova'
          ? 'Entre com a conta que quer usar agora'
          : escolhida
            ? `Entre de novo como ${escolhida.name}`
            : 'Escolha a conta que quer usar agora'
      }
      size="small"
      // A lista encosta nas bordas: sem recuo, o primeiro e o ultimo item nao
      // ganham borda nem folga sobrando.
      flushBody={escolhendo}
      // Tela cheia no celular, janela no desktop: entrar e uma tarefa
      // inteira, e meia tela deixaria o teclado por cima do formulario.
      className="h-[100dvh] max-h-none rounded-none sm:h-auto sm:max-h-[90dvh] sm:max-w-sm sm:rounded-lg"
    >
      {escolhendo ? (
        <AccountPicker
          contas={outras}
          onPick={(conta) => {
            if (conta.conectada) {
              trocar.mutate(conta.id);
              onClose();
              return;
            }

            setEscolhida({ ...conta, image: null });
            setModo('form');
          }}
          onNova={() => {
            setEscolhida(null);
            setModo('form');
          }}
          onRemove={(conta) => {
            forgetAccount(conta.id);
            setEsquecidas((atual) => [...atual, conta.id]);
          }}
          vazio="Nenhuma outra conta entrou neste navegador."
        />
      ) : (
        /* Sem sessao paralela: quem nao esta conectada entra pelo formulario -
           a lista existe para nao ter de lembrar o email. */
        <LoginForm
          defaultEmail={escolhida?.email ?? ''}
          useRemembered={false}
          onSuccess={onClose}
        />
      )}
    </Modal>
  );
}

/** Contas e a janela de entrar juntas, para onde nao ha menu que se feche. */
export function AccountBlock({ user }: { user: PublicUser }) {
  const [entrando, setEntrando] = useState<KnownAccount | 'nova' | 'trocar' | null>(null);

  return (
    <>
      <AccountSwitcher user={user} onEntrar={setEntrando} />
      {entrando ? (
        <LoginModal alvo={entrando} atual={user} onClose={() => setEntrando(null)} />
      ) : null}
    </>
  );
}

/**
 * Avatar do usuario com o menu da conta. Perfil, preferencias e sair moram
 * aqui - na barra do topo eles competiriam com a busca e, no celular, nao
 * cabem.
 */
export function UserMenu({ user }: { user: PublicUser }) {
  const logout = useLogout();
  const [isOpen, setOpen] = useState(false);
  const [entrando, setEntrando] = useState<KnownAccount | 'nova' | 'trocar' | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const itemClass =
    'flex w-full items-center gap-3 px-4 py-3 text-left text-base transition-colors hover:bg-muted';

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Menu da conta"
        onClick={() => setOpen((open) => !open)}
        className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-primary text-base font-semibold text-primary-foreground transition-opacity hover:opacity-90"
      >
        {/* Trocou a foto no perfil, ela aparece aqui tambem - o avatar do topo
            e onde a pessoa confere de que conta esta usando. */}
        {user.image ? (
          <img src={user.image} alt="" className="h-full w-full object-cover" />
        ) : (
          getInitials(user.name)
        )}
      </button>

      {isOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-b-xl border border-border bg-background"
        >
          <div className="border-b border-border">
            <AccountSwitcher
              user={user}
              onEntrar={(alvo) => {
                setOpen(false);
                setEntrando(alvo);
              }}
            />
          </div>

          <Link
            role="menuitem"
            to={SETTINGS_ROUTES.PROFILE}
            className={itemClass}
            onClick={() => setOpen(false)}
          >
            <UserRound className="h-5 w-5 text-muted-foreground" />
            Meu perfil
          </Link>

          <Link
            role="menuitem"
            to={SETTINGS_ROUTES.APPEARANCE}
            className={itemClass}
            onClick={() => setOpen(false)}
          >
            <Palette className="h-5 w-5 text-muted-foreground" />
            Preferencias
          </Link>

          <button
            type="button"
            role="menuitem"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
            className={cn(itemClass, 'border-t border-border text-destructive')}
          >
            {logout.isPending ? <Spinner className="h-5 w-5" /> : <LogOut className="h-5 w-5" />}
            Sair
          </button>
        </div>
      ) : null}

      {entrando ? (
        <LoginModal alvo={entrando} atual={user} onClose={() => setEntrando(null)} />
      ) : null}
    </div>
  );
}
